import { createStreamTelemetryReporter } from "./reportStreamTelemetry";
import firebase from "firebase/app";
import "firebase/auth";
import {
  CONTENT_POLICY_ERROR,
  CONTENT_POLICY_ERROR_CODE,
  CONTENT_GENERATION_FAILED,
} from "../../../../src/shared/constants";
import { ContentViolationError } from "src/lib/Errors";
import { parseJsonResponse } from "src/Auth";
import type { AgenticVercelChatSendRequest } from "./agenticVercelChatBridge";
import { AgenticVercelStreamConsumer } from "./agenticVercelStreamConsumer";
import { agenticStreamRequestHeaders, bypassAgenticStreamProxyInDev } from "./agenticStreamHttp";
import { runResumableAgenticStream } from "./resumableAgenticStream";

function streamPayloadErrorMessage(errorField: unknown): string | null {
  if (errorField == null) return null;
  if (typeof errorField === "string" && errorField.trim()) {
    return errorField.trim();
  }
  if (typeof errorField === "object") {
    const record = errorField as Record<string, unknown>;
    if (typeof record.message === "string" && record.message.trim()) {
      return record.message.trim();
    }
    if (typeof record.error === "string" && record.error.trim()) {
      return record.error.trim();
    }
    if (record.error && typeof record.error === "object") {
      return streamPayloadErrorMessage(record.error);
    }
  }
  return null;
}

/**
 * Resumable Vercel UI message stream transport for agentic chat endpoints.
 * Adapts wire parts to legacy-shaped chunks for persistentChatStore handlers.
 */
export async function runAgenticVercelStreamRequest(request: AgenticVercelChatSendRequest): Promise<void> {
  const url = bypassAgenticStreamProxyInDev(request.url);
  const params = { ...request.body };
  const getIdToken = () => firebase.auth().currentUser!.getIdToken(false);

  const vercelConsumer = new AgenticVercelStreamConsumer({
    onChunk: (payload) => {
      if (payload.error) {
        if (payload.error === CONTENT_GENERATION_FAILED) {
          throw new Error(payload.error, { cause: new ContentViolationError() });
        }
        const msg =
          streamPayloadErrorMessage(payload.error)
          ?? (typeof payload.message === "string" && payload.message.trim()
            ? payload.message.trim()
            : null)
          ?? "An error has occurred. Please try again later.";
        throw new Error(msg);
      }
      request.onChunk(payload);
    },
  });

  const streamReporter = createStreamTelemetryReporter();
  const createInitialResponse = async () => {
    const idToken = await getIdToken();
    streamReporter.setToken(idToken);
    const response = await fetch(url, {
      headers: agenticStreamRequestHeaders(),
      method: "POST",
      body: JSON.stringify({ ...params, idToken }),
      signal: request.signal ?? undefined,
      credentials: "include",
    });

    if (response.status === CONTENT_POLICY_ERROR_CODE) {
      throw new Error(CONTENT_POLICY_ERROR, { cause: new ContentViolationError() });
    }
    if (response.status >= 400) {
      const json = await parseJsonResponse(response, url);
      throw Object.assign(new Error(json.message || json.error || "An error has occurred. Please try again later."), {
        status: response.status, code: json.code,
      });
    }

    request.onStart?.();
    return response;
  };

  const createResumeResponse = async (
    chatId: string,
    lastEventId: string | null,
    userMessageId?: string,
  ) => {
    const idToken = await getIdToken();
    streamReporter.setToken(idToken);
    const resumePath = `/api/ai/agenticChat/${encodeURIComponent(chatId)}/stream${
      userMessageId ? `?userMessageId=${encodeURIComponent(userMessageId)}` : ""
    }`;
    return fetch(bypassAgenticStreamProxyInDev(resumePath), {
      headers: {
        ...agenticStreamRequestHeaders(),
        Accept: "text/event-stream",
        Authorization: `Bearer ${idToken}`,
        ...(lastEventId ? { "Last-Event-ID": lastEventId } : {}),
      },
      method: "GET",
      signal: request.signal ?? undefined,
      credentials: "include",
    });
  };

  await runResumableAgenticStream({
    clientRequestId: typeof params.clientRequestId === "string" ? params.clientRequestId : undefined,
    onTelemetry: streamReporter.report,
    createInitialResponse,
    createResumeResponse,
    initialChatId: typeof params.chatId === "string" && params.chatId ? params.chatId : null,
    signal: request.signal,
    handlers: {
      onVercelPart: (part) => {
        vercelConsumer.consume(part);
      },
      onDone: () => {
        request.onDone();
      },
      onFailure: (error) => {
        const err = error as Error & { cause?: unknown; status?: number; responseFailureKind?: string; code?: string };
        request.onError({
          error: true,
          message: err?.message,
          cause: err?.cause,
          status: err?.status,
          code: err?.code,
          responseFailureKind: err?.responseFailureKind,
        });
      },
    },
  });
}
